import { X } from "lucide-react";
import { useState } from "react";
import Moveable from "react-moveable";
import { useRef } from "react";
import useUserStore, { TextField } from "@/state/user/useUserStore";
import {
  Title1,
  Title2,
  Title3,
  Title4,
  Title5,
  Title6,
  Title7,
  Title8,
  Title9,
  Title10,
  Title11,
  Title12,
  Title13,
  Title14,
  Title15,
} from "./text-styles";
import { HoverBtn } from "@/components/styles";
import { lightenColor } from "@/utils/colorUtils";
import { useMediaQuery } from "@mantine/hooks";

export const MoveableText: React.FC<{
  textField: TextField;
  hideControls: boolean;
  color: string;
  startPosition?: {
    x: string;
    y: string;
  };
  width?: string;
}> = ({ textField, hideControls, color, startPosition, width }) => {
  const targetRef = useRef<HTMLDivElement | null>(null);

  const [removeTextField] = useUserStore((state) => [state.removeTextField]);

  const [isHovered, setIsHovered] = useState(false);
  const [frame, setFrame] = useState({
    translate: [0, 0],
    scale: [1, 1],
    width: targetRef.current?.clientWidth,
    height: targetRef.current?.clientHeight,
    rotate: 0,
  });

  const [bounds, setBounds] = useState({
    left: 10,
    top: 10,
    right: 10,
    bottom: 10,
  });

  const fontComponents = {
    title1: Title1,
    title2: Title2,
    title3: Title3,
    title4: Title4,
    title5: Title5,
    title6: Title6,
    title7: Title7,
    title8: Title8,
    title9: Title9,
    title10: Title10,
    title11: Title11,
    title12: Title12,
    title13: Title13,
    title14: Title14,
    title15: Title15,
  };

  const SelectedTitle =
    fontComponents[textField.font as keyof typeof fontComponents] || Title1;

  const lighterColor = lightenColor(color, 10);

  const isSmallScreen = useMediaQuery("(max-width: 768px)");

  return (
    <>
      <div
        style={{
          width: width || "100%",
          height: "auto",
          minHeight: "96px",
          position: "absolute",
          left: startPosition?.x || "0px",
          top: startPosition ? "auto" : "0px",
          bottom: startPosition ? startPosition.y : "auto",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          zIndex: textField.zIndex,
          pointerEvents: hideControls ? "none" : "auto",
        }}
        onMouseEnter={() =>
          !isSmallScreen && !hideControls && setIsHovered(true)
        }
        onMouseLeave={() =>
          !isSmallScreen && !hideControls && setIsHovered(false)
        }
        ref={targetRef}
      >
        {isHovered && !hideControls && (
          <div
            style={{
              position: "absolute",
              top: 0,
              right: 0,
              zIndex: 100,
            }}
          >
            <HoverBtn
              color={color}
              hoverColor={lighterColor}
              onClick={() => removeTextField(textField.id)}
            >
              <X style={{ width: "12px", height: "12px" }} />
            </HoverBtn>
          </div>
        )}
        <SelectedTitle
          fontSize={textField.fontSize}
          color={textField.color !== "#ffffff" ? textField.color : ""}
        >
          {textField.text}
        </SelectedTitle>
      </div>
      <Moveable
        target={hideControls ? null : targetRef.current}
        draggable={true}
        resizable={true}
        scalable={true}
        rotatable={true}
        onDrag={({ beforeTranslate }) => {
          frame.translate = beforeTranslate;
        }}
        onResize={({ width, height }) => {
          frame.width = width;
          frame.height = height;
        }}
        onScale={({ scale }) => {
          frame.scale = scale;
          if (targetRef.current) {
            if (scale[0] < 0) {
              targetRef.current.style.transform = `scaleX(-1)`;
            } else {
              targetRef.current.style.transform = `scaleX(1)`;
            }
          }
        }}
        bounds={bounds}
        onRotate={({ beforeRotate }) => {
          frame.rotate = beforeRotate;
        }}
        onRender={({ target }) => {
          target.style.transform = `translate(${frame.translate[0]}px, ${frame.translate[1]}px) scale(${frame.scale[0]}, ${frame.scale[1]}) rotate(${frame.rotate}deg)`;
          target.style.width = `${frame.width}px`;
          target.style.height = `${frame.height}px`;
        }}
      />
    </>
  );
};
